export type TallyTab = 'assets' | 'inventory' | 'fleet'

export type TallyModalMode = 'view' | 'edit' | 'add'

/** Inventory lifecycle statuses (value stored in DB → label shown in UI). */
export const INVENTORY_STATUSES = [
  { value: 'in_stock', label: 'In stock' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'on_site', label: 'On site' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'depleted', label: 'Depleted' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'retired', label: 'Retired' },
] as const

export type InventoryStatus = (typeof INVENTORY_STATUSES)[number]['value']

export function formatInventoryStatus(status?: string | null): string {
  if (!status) return '—'
  const known = INVENTORY_STATUSES.find((s) => s.value === status)
  if (known) return known.label
  if (status === 'out_of_stock') return 'Out of stock'
  return status.replace(/_/g, ' ')
}

/** Flatten item fields into a lowercase haystack for client-side search. */
export function buildTallySearchHaystack(
  item: AssetOrInventory,
  extras?: { assignmentLabel?: string | null },
): string {
  const parts: Array<string | number | null | undefined> = [
    item.name,
    item.type,
    item.status,
    item.source === 'Inventory' ? formatInventoryStatus(item.status) : item.status,
    item.notes,
    item.sku,
    item.barcode,
    item.serialNumber,
    item.project,
    item.jobId,
    item.jobNumber,
    item.jobName,
    formatJobLabel(item),
    item.location?.address,
    item.assignedTo,
    item.assignedEmployeeId,
    extras?.assignmentLabel,
    item.quantity,
    item.value,
    item.externalSource,
    item.externalKey,
    item.externalIdentifiers?.vin,
    item.externalIdentifiers?.serialNumber,
    item.externalIdentifiers?.imei,
    item.externalIdentifiers?.deviceUUID,
    item.externalIdentifiers?.trackerId,
    ...(Array.isArray(item.documents) ? item.documents.map((d) => d.fileName) : []),
  ]
  return parts
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
    .map((v) => String(v).toLowerCase())
    .join(' ')
}

/** True when every whitespace token in `query` appears in `haystack`. */
export function matchesTallySearch(haystack: string, query: string): boolean {
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
  if (!tokens.length) return true
  return tokens.every((token) => haystack.includes(token))
}

export type JobTreadJobOption = {
  id?: string
  number?: string
  name?: string
  location?: { address?: string }
  /** Some JobTread payloads put address on the job root */
  address?: string
  locationName?: string
}

export type InventoryHistoryEntry = {
  _id: string
  inventoryId: string
  action: 'created' | 'check_to' | 'updated'
  fromJob?: { jobId?: string; jobNumber?: string; jobName?: string }
  toJob?: { jobId?: string; jobNumber?: string; jobName?: string }
  userId?: string
  userName?: string
  note?: string
  createdAt?: string
}

export type InventoryDocument = {
  fileName: string
  mimeType: string
  dataUrl: string
  size?: number
}

export type AssetOrInventory = {
  _id: string
  name: string
  type: string
  assignedTo?: string
  assignedEmployeeId?: string
  dateAssigned?: string
  location?: {
    address?: string
    coordinates?: [number, number]
    type?: string
  } | null
  value?: number
  status?: string
  serialNumber?: string
  notes?: string
  quantity?: number
  sku?: string
  source: 'Asset' | 'Inventory'
  image?: string
  img?: string
  imageUrls?: string[]
  documents?: InventoryDocument[]
  project?: string
  barcode?: string
  jobId?: string
  jobNumber?: string
  jobName?: string
  externalIdentifiers?: {
    vin?: string
    serialNumber?: string
    imei?: string
    deviceUUID?: string
    trackerId?: string
  }
  externalSource?: string
  externalKey?: string
  lastSyncedAt?: string
  lastSeenAt?: string
  createdAt?: string
  updatedAt?: string
}

export type EmployeeAssignmentOption = {
  employeeId: string
  displayName: string
}

/** Keep low for mobile PWA RAM — phone cameras are often 12MP+. */
export const MAX_IMAGE_DIMENSION = 1280
export const IMAGE_JPEG_QUALITY = 0.72
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024
export const DOCUMENT_ACCEPT =
  'image/*,.pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const ALLOWED_DOC_MIME_PREFIXES = ['image/']
const ALLOWED_DOC_MIME_TYPES = new Set([
  'application/pdf',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
])
const ALLOWED_DOC_EXTENSIONS = new Set(['pdf', 'csv', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'])

export function getImageUrls(item: AssetOrInventory | null | undefined): string[] {
  if (!item) return []
  if (Array.isArray(item.imageUrls) && item.imageUrls.length) return item.imageUrls.filter(Boolean)
  return [item.image, item.img].filter(Boolean) as string[]
}

export function getDocuments(item: AssetOrInventory | null | undefined): InventoryDocument[] {
  if (!item || !Array.isArray(item.documents)) return []
  return item.documents.filter((d) => d?.dataUrl && d?.fileName)
}

function fileExtension(name: string): string {
  const parts = String(name || '').toLowerCase().split('.')
  return parts.length > 1 ? parts[parts.length - 1] : ''
}

export function isAllowedDocumentFile(file: File): boolean {
  const mime = String(file.type || '').toLowerCase()
  const ext = fileExtension(file.name)
  if (ALLOWED_DOC_MIME_PREFIXES.some((p) => mime.startsWith(p))) return true
  if (mime && ALLOWED_DOC_MIME_TYPES.has(mime)) return true
  return ALLOWED_DOC_EXTENSIONS.has(ext)
}

export async function readDocumentFiles(files: FileList | File[]): Promise<InventoryDocument[]> {
  const list = Array.from(files)
  const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const out: InventoryDocument[] = []
  for (const file of list) {
    if (!isAllowedDocumentFile(file)) {
      throw new Error(`Unsupported file type: ${file.name}`)
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new Error(`${file.name} is too large (max 5 MB)`)
    }
    const dataUrl = await fileToDataUrl(file)
    out.push({
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      dataUrl,
      size: file.size,
    })
  }
  return out
}

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatJobLabel(item: Pick<AssetOrInventory, 'jobName' | 'jobNumber' | 'jobId' | 'project'>): string | null {
  if (item.jobName && item.jobNumber) return `${item.jobNumber} — ${item.jobName}`
  if (item.jobName) return item.jobName
  if (item.jobNumber) return `Job ${item.jobNumber}`
  if (item.project) return item.project
  return null
}

export function jobOptionFromItem(
  item: Pick<AssetOrInventory, 'jobId' | 'jobNumber' | 'jobName' | 'location'>,
): JobTreadJobOption | null {
  if (!item.jobId && !item.jobNumber && !item.jobName) return null
  return {
    id: item.jobId,
    number: item.jobNumber,
    name: item.jobName,
    location: item.location?.address ? { address: item.location.address } : undefined,
  }
}

/** Normalize JobTread search hits (address may be top-level from the JT integration). */
export function normalizeJobOption(raw: JobTreadJobOption & { address?: string; locationName?: string }): JobTreadJobOption {
  const address =
    raw.location?.address ||
    raw.address ||
    raw.locationName ||
    ''
  return {
    id: raw.id != null ? String(raw.id) : undefined,
    number: raw.number != null ? String(raw.number) : undefined,
    name: raw.name != null ? String(raw.name) : undefined,
    location: address ? { address: String(address) } : undefined,
  }
}

export function locationAddressFromJob(job: JobTreadJobOption | null | undefined): string {
  if (!job) return ''
  return (
    job.location?.address ||
    [job.number, job.name].filter(Boolean).join(' — ') ||
    ''
  )
}

/**
 * Downscale + JPEG-encode photos for Tally.
 * Processes one file at a time and prefers createImageBitmap resize options so
 * we never hold a full-resolution decoded bitmap (common OOM on phone PWAs).
 */
export async function compressImageFiles(files: FileList | File[]): Promise<string[]> {
  const list = Array.from(files)
  const out: string[] = []
  for (const file of list) {
    out.push(await compressImageFile(file))
  }
  return out
}

async function compressImageFile(file: File): Promise<string> {
  if (typeof createImageBitmap !== 'function') {
    return compressViaHtmlImage(file, MAX_IMAGE_DIMENSION)
  }

  const bitmap = await decodeImageBitmapDownscaled(file, MAX_IMAGE_DIMENSION)
  try {
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Unable to process image')
    ctx.drawImage(bitmap, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY)
    canvas.width = 0
    canvas.height = 0
    return dataUrl
  } finally {
    bitmap.close()
  }
}

function fitWithin(width: number, height: number, maxDim: number): { width: number; height: number } {
  const scale = Math.min(1, maxDim / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/** Read width/height from JPEG/PNG headers without decoding pixels. */
async function probeImageSize(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const header = new Uint8Array(await file.slice(0, 65536).arrayBuffer())
    // PNG: IHDR at byte 16
    if (
      header.length >= 24 &&
      header[0] === 0x89 &&
      header[1] === 0x50 &&
      header[2] === 0x4e &&
      header[3] === 0x47
    ) {
      const width = (header[16] << 24) | (header[17] << 16) | (header[18] << 8) | header[19]
      const height = (header[20] << 24) | (header[21] << 16) | (header[22] << 8) | header[23]
      if (width > 0 && height > 0) return { width, height }
    }
    // JPEG: scan for SOF0/SOF2
    if (header.length > 4 && header[0] === 0xff && header[1] === 0xd8) {
      let i = 2
      while (i < header.length - 8) {
        if (header[i] !== 0xff) {
          i += 1
          continue
        }
        const marker = header[i + 1]
        if (marker === 0xc0 || marker === 0xc2) {
          const height = (header[i + 5] << 8) | header[i + 6]
          const width = (header[i + 7] << 8) | header[i + 8]
          if (width > 0 && height > 0) return { width, height }
          break
        }
        if (marker === 0xd9 || marker === 0xda) break
        const segLen = (header[i + 2] << 8) | header[i + 3]
        if (segLen < 2) break
        i += 2 + segLen
      }
    }
  } catch {
    /* ignore — fall back to full decode */
  }
  return null
}

async function decodeImageBitmapDownscaled(file: File, maxDim: number): Promise<ImageBitmap> {
  const probed = await probeImageSize(file)
  if (probed) {
    const target = fitWithin(probed.width, probed.height, maxDim)
    if (target.width < probed.width || target.height < probed.height) {
      try {
        return await createImageBitmap(file, {
          resizeWidth: target.width,
          resizeHeight: target.height,
          resizeQuality: 'medium',
        })
      } catch {
        /* fall through */
      }
    }
  }

  const full = await createImageBitmap(file)
  const target = fitWithin(full.width, full.height, maxDim)
  if (target.width === full.width && target.height === full.height) return full
  try {
    const next = await createImageBitmap(full, {
      resizeWidth: target.width,
      resizeHeight: target.height,
      resizeQuality: 'medium',
    })
    full.close()
    return next
  } catch {
    return full
  }
}

function compressViaHtmlImage(file: File, maxDim: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      try {
        const target = fitWithin(img.width || 1, img.height || 1, maxDim)
        const canvas = document.createElement('canvas')
        canvas.width = target.width
        canvas.height = target.height
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Unable to process image')
        ctx.drawImage(img, 0, 0, target.width, target.height)
        URL.revokeObjectURL(url)
        const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY)
        canvas.width = 0
        canvas.height = 0
        resolve(dataUrl)
      } catch (e) {
        URL.revokeObjectURL(url)
        reject(e)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image. Try a smaller photo or choose from gallery.'))
    }
    img.src = url
  })
}

export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'string' && err) return err
  return fallback
}
